/** Display labels and descriptions for challenge metadata (shared across pages). */

export const CHALLENGE_TYPES = {
    coop: 'Co-Op',
    ranked: 'Ranked',
};

export const CHALLENGE_TYPE_DESC = {
    coop: 'All participants collectively reach the SP goal',
    ranked: '1st, 2nd, 3rd place with a minimum SP goal',
};

export const VISIBILITY_LABELS = {
    public: 'Public',
    friends_only: 'Friends Only',
    invite_only: 'Invite Only',
};

export const VISIBILITY_DESC = {
    public: 'Anyone can find and join this challenge',
    friends_only: 'Only friends can find and join',
    invite_only: 'Only people you invite can join',
};

export const formatSp = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return (Math.round(n * 100) / 100).toLocaleString('en-US');
};

/** Split an ISO timestamp into a short date and a clock time for stat tiles. */
export const splitChallengeDate = (iso) => {
    if (!iso) return { value: '—', unit: '' };
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return { value: '—', unit: '' };
    return {
        value: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        unit: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    };
};

export const challengeStartIso = (challenge) => {
    if ((challenge.max_recurrences || 0) > 0 && challenge.current_week_start) {
        return challenge.current_week_start;
    }
    return challenge.start_time_utc || null;
};

export const challengeEndIso = (challenge) => {
    if (challenge.current_week_end) return challenge.current_week_end;
    if (!challenge.start_time_utc) return null;
    const duration = Number(challenge.duration) || 7;
    const start = new Date(challenge.start_time_utc);
    if (Number.isNaN(start.getTime())) return null;
    return new Date(start.getTime() + duration * 86400000).toISOString();
};

/**
 * Equal-column summary stats shared by challenge cards and the detail page.
 * Card view is four cells. Detail view adds a second row of four.
 */
export const challengeStatItems = (challenge, { detailed = false } = {}) => {
    const totalWeeks = challenge.total_runs || ((challenge.max_recurrences || 0) + 1);
    const currentWeek = challenge.current_recurrence || 1;
    const count = challenge.participant_count ?? (
        Array.isArray(challenge.participants) ? challenge.participants.length : 0
    );
    const peopleUnit = challenge.max_participants ? `of ${challenge.max_participants}` : 'joined';

    const items = [
        {
            label: 'Type',
            value: CHALLENGE_TYPES[challenge.challenge_type] || challenge.challenge_type || '—',
        },
        {
            label: 'Goal',
            value: formatSp(challenge.sp_goal),
            unit: 'SP',
            tone: 'points',
        },
        {
            label: 'Week',
            value: String(currentWeek),
            unit: `of ${totalWeeks}`,
        },
        {
            label: 'People',
            value: String(count),
            unit: peopleUnit,
        },
    ];

    if (!detailed) return items;

    const start = splitChallengeDate(challengeStartIso(challenge));
    const end = splitChallengeDate(challengeEndIso(challenge));
    items.push(
        { label: 'Starts', value: start.value, unit: start.unit },
        { label: 'Ends', value: end.value, unit: end.unit },
        { label: 'Length', value: '7', unit: 'days' },
        {
            label: 'Visibility',
            value: VISIBILITY_LABELS[challenge.visibility] || challenge.visibility || '—',
        },
    );
    return items;
};

export const STATUS_LABELS = {
    upcoming: 'Upcoming',
    active: 'Active',
    completed: 'Completed',
    ended: 'Ended',
};
